import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { useGetSubmittedLoans } from "../../hooks";
import {
  AdditionalInfo,
  Company,
  DrawdownStatus,
  LoanApplicationCaseStatus,
  LoanApplicationStatus,
  LoanProductType,
} from "../../hooks/api/types";

export enum LoanPaymentStatus {
  PAID = "PAID",
  SCHEDULED = "SCHEDULED",
  OVERDUE = "OVERDUE",
  DUE = "DUE",
  MISSED = "MISSED",
  LATE_FEE_APPLIED = "LATE_FEE_APPLIED",
  PARTIAL_PAYMENT = "PARTIAL_PAYMENT",
  EXCESS_PAYMENT = "EXCESS_PAYMENT",
}

export enum FacilityTab {
  OVERVIEW = "overview",
  VENDORS = "vendors",
  DOCUMENTS = "documents",
  SETTINGS = "settings",
}

export const statusReadableMap: Record<LoanPaymentStatus, string> = {
  [LoanPaymentStatus.PAID]: "Paid",
  [LoanPaymentStatus.SCHEDULED]: "Scheduled",
  [LoanPaymentStatus.OVERDUE]: "Overdue",
  [LoanPaymentStatus.DUE]: "Due",
  [LoanPaymentStatus.LATE_FEE_APPLIED]: "Late Fee Applied",
  [LoanPaymentStatus.PARTIAL_PAYMENT]: "Partial Payment",
  [LoanPaymentStatus.EXCESS_PAYMENT]: "Excess Payment",
  [LoanPaymentStatus.MISSED]: "Missed",
};

export interface LoanPayment {
  amount: number;
  date: string;
  id: string;
  loanApplicationId: string;
  status: LoanPaymentStatus;
  outstandingAmount: number;
  exceededAmount?: number;
  installmentId?: number;
}

interface LoanProductConfig {
  duration: {
    min: number;
    max: number;
    initial: number;
    interval: number;
    unit: string;
  };
  amount: {
    min: number;
    max: number;
    initial: number;
    interval: number;
    unit: string;
  };
  interestRate: {
    value: number;
    period: number;
  };
}

export enum InterestFrequency {
  DAILY = "DAILY",
  MONTHLY = "MONTHLY",
  ANNUALLY = "ANNUALLY",
}
export enum LOSPlatform {
  BIZ2X = "BIZ2X",
  CREDIBLEX = "CREDIBLEX",
}

export enum LoanRequestPurpose {
  LOAN = "LOAN",
  CREDIT_LINE = "CREDIT_LINE",
}
export interface LoanApplication {
  id: string;
  companyId: string;
  distributionPartnerId: string;
  userId: string;
  stage: string;
  loanProductId: string;
  loanInput: {
    amount: number;
    duration: number;
    loanType: string;
  };
  loanProduct: {
    id: string;
    displayName: string;
    type: string;
    config: LoanProductConfig;
  };
  loanPayments: Array<LoanPayment>;
  status: LoanApplicationStatus;
  createdAt: string;
  loanType: LoanProductType;
  additionalInfo: AdditionalInfo[];
  externalId: number;
  submittedAt: string;
  nextPaymentDate: string;
  loanClosureDate?: string;
  amount?: number;
  paidAmount?: number;
  remainingAmount?: number;
  availableLimit?: number;
  loanOfferInterestRate?: number;
  interestFrequency?: InterestFrequency;
  company?: Company;
  creditLine?: {
    approvedLimit: number;
    requestedLimit?: number;
    type?: string;
    status?: string;
    currency?: string;
    interestRate?: number;
    duration?: number;
    durationType?: string;
    availableAmount?: number;
    consumedAmount?: number;
    approvedAmount?: number;
    startDate?: string;
    endDate?: string;
    outstandingAmount?: number;
    cashMarginAmount?: number;
    advancePercentage?: number;
    lateFee?: number;
  };
  [key: string]: any;
  losPlatform?: LOSPlatform;
  caseStatus: LoanApplicationCaseStatus;
  approvedAmount?: number;
  drawdownEligibility?: { isValid: boolean; reason?: string };
  requestPurpose?: LoanRequestPurpose;
}

export type DrawdownDecision = {
  invoiceNumber?: string;
  fundableAmount?: number | null;
  invoiceAmount?: number | null;
};

export type Vendor = {
  id: string;
  name: string;
};

export type CreditLine = {
  id: string;
  type?: string;
};
export type DrawdownBatch = {
  id: string;
  batchId?: number;
  vendorId?: string;
  invoiceNumber?: string;
  invoiceAmount?: number;
  requestedAmount?: number;
  approvedAmount?: number | null;
  outstandingAmount?: number | null;
  discountPercentage?: number | null;
  status: DrawdownStatus;
  issuedDate?: string;
  dueDate?: string;
  vendor?: Vendor;
  currency?: string;
  decision?: DrawdownDecision;
  creditLine?: CreditLine;
};

type LoanDashboardContextValues = {
  state: {
    loans: Array<LoanApplication>;
    loading: boolean;
    filterLoading: boolean;
    currentLoanApplicationId: string;
    currentLoanApplication: LoanApplication | undefined;
    currentPaymentIds: string[];
    showPaymentDetails: boolean;
    showPaymentSchedule: boolean;
    showPaymentHistory: boolean;
    showTransactionHistory: boolean;
    showDrawdownForm: boolean;
    showAdditionalInfoRequired: boolean;
    showDrawdownSuccess: boolean;
    showFacilityOverview: boolean;
    facilityActiveTab: string;
  };
  actions: {
    update: (
      payload: Partial<LoanDashboardContextValues["state"]>
    ) => Promise<any>;
    refetch: (params?: Record<string, any>) => Promise<any>;
  };
};

type LoanDashboardContextAction = {
  type: "UPDATE";
  payload: Partial<LoanDashboardContextValues["state"]>;
};

const INITIAL_STATE: LoanDashboardContextValues["state"] = {
  loans: [],
  loading: false,
  filterLoading: false,
  currentLoanApplicationId: "",
  currentLoanApplication: undefined,
  currentPaymentIds: [],
  showPaymentSchedule: false,
  showPaymentHistory: false,
  showPaymentDetails: false,
  showTransactionHistory: false,
  showDrawdownForm: false,
  showAdditionalInfoRequired: false,
  showDrawdownSuccess: false,
  showFacilityOverview: false,
  facilityActiveTab: FacilityTab.OVERVIEW,
};

const LoanDashboardContext = createContext<LoanDashboardContextValues>({
  state: INITIAL_STATE,
  actions: {
    update: async () => {},
    refetch: async () => {},
  },
});

const reducer = (
  state: LoanDashboardContextValues,
  action: LoanDashboardContextAction
): LoanDashboardContextValues => {
  switch (action.type) {
    case "UPDATE":
      return {
        ...state,
        state: {
          ...state.state,
          ...action.payload,
        },
      };
    default:
      return state;
  }
};

export const LoanDashboardProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, {
    state: INITIAL_STATE,
    actions: {
      update: async () => {},
      refetch: async () => {},
    },
  });

  const { mutateAsync: getApplications } = useGetSubmittedLoans();

  function fetchAndPopulateDashboardContext(params?: Record<string, any>) {
    const isFilterRefetch = params !== undefined;
    const hasFilterParams = params != null && Object.keys(params).length > 0;
    dispatch({
      type: "UPDATE",
      payload: isFilterRefetch ? { filterLoading: true } : { loading: true },
    });
    getApplications(hasFilterParams ? params : undefined)
      .then((res) => {
        const loanList = res?.loanApplications;
        dispatch({
          type: "UPDATE",
          payload: {
            loans: loanList,
            loading: false,
            filterLoading: false,
          },
        });
      })
      .catch((err) => {
        dispatch({
          type: "UPDATE",
          payload: { loading: false, filterLoading: false },
        });
      });
  }

  useEffect(() => {
    fetchAndPopulateDashboardContext();
  }, []);

  const actions = useMemo(() => {
    return {
      update: async (payload: Partial<LoanDashboardContextValues["state"]>) => {
        dispatch({ type: "UPDATE", payload });
      },
      refetch: async (params?: Record<string, any>) => {
        fetchAndPopulateDashboardContext(params);
      },
    };
  }, [dispatch]);

  const contextValue = useMemo(() => {
    return { state: state.state, actions };
  }, [state.state]);

  return (
    <LoanDashboardContext.Provider value={contextValue}>
      {children}
    </LoanDashboardContext.Provider>
  );
};

export const useLoanDashboard = () => useContext(LoanDashboardContext);
